/**
 * Spotlight anchored to the "Recent" item in the main nav (ACTIVATION-2059).
 * Shown only when: eligible for simplify-nav experiment, treatment variant, D1+,
 * Recent is in main nav, not seen/dismissed, and user hasn't opened customize dialog.
 */
import React, { useCallback, useState, type PropsWithChildren } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import {
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
	UNSAFE_UpdateOnChange as SpotlightUpdateOnChange,
} from '@atlaskit/spotlight';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import {
	type SpotlightSource,
	useSimplifyNavNewUserSpotlightStore,
} from './hooks/useSimplifyNavNewUserSpotlightStore.ts';

const spotlightHeadlineStyles = cssMap({
	headingOneLine: {
		whiteSpace: 'nowrap',
	},
});

const i18n = defineMessages({
	spotlightHeading: {
		id: 'experiment-simplify-nav-new-user.spotlight-heading',
		defaultMessage: 'We added features to your sidebar',
		description:
			'Heading for a spotlight dialog shown to new users, highlighting that new features have been added to the Confluence sidebar navigation',
	},
	spotlightBody: {
		id: 'experiment-simplify-nav-new-user.spotlight-body',
		defaultMessage:
			'Customize what appears here by selecting {more} at the bottom of your sidebar.',
		description:
			'Body text for a spotlight dialog shown to new users. Tells users they can customize the sidebar by selecting the "More" menu. {more} is a placeholder for the bolded "More" menu name.',
	},
	more: {
		id: 'experiment-simplify-nav-new-user.more',
		defaultMessage: 'More',
		description:
			'Name of the "More" menu in the sidebar. This string is passed as the {more} placeholder in the "experiment-simplify-nav-new-user.spotlight-body" message and rendered in bold.',
	},
	gotIt: {
		id: 'experiment-simplify-nav-new-user.got-it',
		defaultMessage: 'Got it',
		description:
			'Text for the button that dismisses the spotlight dialog shown to new users about sidebar features',
	},
});

type SimplifyNavNewUserSpotlightProps = PropsWithChildren<{
	/**
	 * When false, only children are rendered (no spotlight). Passed from parent so the wrapper
	 * stays mounted and Recent item state is preserved when eligibility toggles.
	 * @default false
	 */
	shouldShowSpotlight?: boolean;
	/** Which experiment path is active, for correct persistence and analytics. */
	source?: 'new-user' | 'xflow' | null;
}>;

const SimplifyNavNewUserSpotlightComponent = ({
	children,
	shouldShowSpotlight = false,
	source = 'new-user',
}: SimplifyNavNewUserSpotlightProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { PopoverContent } = useChoreographerAssets();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const [hasDismissed, setHasDismissed] = useState(false);
	const [hasSeenOrDismissed] = useState(() => getHasSeenOrDismissed());
	const [hasOpenedCustomize] = useSimplifyNavNewUserSpotlightStore();

	// Parent gates with useSimplifiedNavSpotlight (eligibility, treatment, D1+). We only check session-specific conditions.
	const shouldShow =
		shouldShowSpotlight &&
		!hasSeenOrDismissed &&
		!hasOpenedCustomize &&
		!hasDismissed &&
		!renderServerPlaceholder;

	const handleDismiss = useCallback(
		(reason: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: `simplifyNavNewUserSpotlight${reason === 'dismiss-control' ? 'Dismiss' : 'CTA'}`,
					source: getMessageId(source),
				},
			}).fire();
			markSeenOrDismissed();
			setHasDismissed(true);
		},
		[createAnalyticsEvent, source],
	);

	// Always render PopoverProvider/PopoverTarget so children's parent never changes,
	// preserving React state (e.g., RecentItem flyout state) when spotlight visibility toggles.
	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			{shouldShow && (
				<>
					<SpotlightUpdateOnChange selectors={['nav#side-navigation']} />
					<PopoverContent
						messageId={getMessageId(source)}
						messageType="engagement"
						placement="right-end"
						offset={[0, 16]}
						// Fires when user presses Escape or clicks outside the spotlight (PopoverContent built-in behavior)
						dismiss={() => handleDismiss('auto')}
						isVisible={shouldShow}
						onMessageDisposition={(status) => {
							if (status === 'started') {
								createAnalyticsEvent({
									type: 'sendScreenEvent',
									data: {
										name: getScreenEventName(source),
										attributes: { source: 'RecentItem' },
									},
								}).fire();
								markSeenOrDismissed();
							}
						}}
					>
						<SpotlightCard testId="simplify-nav-new-user-spotlight">
							<SpotlightHeader>
								<SpotlightHeadline>
									<Box xcss={spotlightHeadlineStyles.headingOneLine}>
										<FormattedMessage {...i18n.spotlightHeading} />
									</Box>
								</SpotlightHeadline>
								<SpotlightControls>
									<SpotlightDismissControl
										onClick={() => handleDismiss('dismiss-control')}
										autoFocus={false}
									/>
								</SpotlightControls>
							</SpotlightHeader>
							<SpotlightBody>
								<Text>
									<FormattedMessage
										{...i18n.spotlightBody}
										values={{
											more: (
												<Text as="strong">
													<FormattedMessage {...i18n.more} />
												</Text>
											),
										}}
									/>
								</Text>
							</SpotlightBody>
							<SpotlightFooter>
								<SpotlightActions>
									<SpotlightPrimaryAction onClick={() => handleDismiss('primary-action')}>
										<FormattedMessage {...i18n.gotIt} />
									</SpotlightPrimaryAction>
								</SpotlightActions>
							</SpotlightFooter>
						</SpotlightCard>
					</PopoverContent>
				</>
			)}
		</PopoverProvider>
	);
};

export const SimplifyNavNewUserSpotlight = ({
	source,
	...rest
}: SimplifyNavNewUserSpotlightProps) => (
	<TransparentErrorBoundary
		attribution={source === 'xflow' ? Attribution.GROWTH_CFIND : Attribution.CE_MAU}
	>
		<SimplifyNavNewUserSpotlightComponent source={source} {...rest} />
	</TransparentErrorBoundary>
);

const getMessageId = (source: SpotlightSource): string =>
	source === 'xflow' ? 'cfind-5565-nav-spotlight' : 'simplify-nav-new-user-spotlight';

const getScreenEventName = (source: SpotlightSource): string =>
	source === 'xflow' ? 'Cfind5565NavSpotlight' : 'SimplifyNavNewUserSpotlight';

const getHasSeenOrDismissed = (): boolean => {
	try {
		return (
			localStorage?.getItemAsBoolean(
				PERSISTED_KEYS_ON_SERVER.PERSISTED_SIMPLIFY_NAV_NEW_USER_SPOTLIGHT_DISMISSED,
			) ?? false
		);
	} catch {
		return false;
	}
};

const markSeenOrDismissed = (): void => {
	try {
		localStorage?.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_SIMPLIFY_NAV_NEW_USER_SPOTLIGHT_DISMISSED,
			'true',
		);
	} catch {
		// storage unavailable
	}
};
